// src/features/profile/services/profileService.js
// Consolidated Profile Service - 2026 Architecture
//
// Single source of truth: userProfiles/{uid}
// Legacy collections (profiles, userInsightPreferences, etc.) are kept only
// for GDPR deletion — no writes or reads in normal app flow.
//
// Schema version: 3.1 (adds firstName, emailVerified, timezone, consent, activity timestamps)

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import { getUserTimezone } from '@/shared/utils/core/timezone';
import logger from '@/shared/utils/core/logger';
import { buildEnhancedAIContext } from '../utils/profileContextBuilder';

// Schema version for migration tracking
const PROFILE_SCHEMA_VERSION = '3.1';

/**
 * Default preferences structure
 */
const DEFAULT_PREFERENCES = {
  insights: {
    frequency: 'balanced',
    paused: false,
    pausedUntil: null
  },
  journal: {
    defaultPrivacy: 'private',
    reminderEnabled: false,
    reminderTime: null
  },
  ocr: {
    keepOriginalImages: true
  },
  privacy: {
    dataRetention: '2_years',
    shareAnalytics: false
  }
};

/**
 * Default consent structure — null timestamps until the user explicitly accepts
 */
const DEFAULT_CONSENT = {
  termsAcceptedAt: null,
  privacyPolicyAcceptedAt: null,
  ageVerified: false,
  ageConsentAt: null
};

function generateSyncToken() {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function removeUndefined(obj) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined).filter(v => v !== undefined);

  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (typeof value === 'object' && value !== null) {
        const cleanedValue = removeUndefined(value);
        if (cleanedValue !== null && (Array.isArray(cleanedValue) || Object.keys(cleanedValue).length > 0)) {
          cleaned[key] = cleanedValue;
        }
      } else {
        cleaned[key] = value;
      }
    }
  }
  return cleaned;
}

/**
 * Create initial profile for new users.
 * Idempotent: checks for existing doc before writing to avoid race-condition overwrites.
 *
 * @param {string} userId
 * @param {Object} data - { displayName, email, photoURL, provider, emailVerified }
 * @returns {Promise<Object>} - Created or existing profile data
 */
export async function createInitialProfile(userId, data = {}) {
  if (!userId) throw new Error('User ID is required');

  try {
    logger.info('🆕 [ProfileService] Creating initial profile for:', userId);

    // Idempotency guard — don't overwrite an existing profile
    const existing = await getDoc(doc(db, COLLECTIONS.USER_PROFILES, userId));
    if (existing.exists()) {
      logger.debug('[ProfileService] Profile already exists, skipping creation');
      return existing.data();
    }

    const displayName = data.displayName || '';
    const firstName = displayName.split(' ')[0] || '';

    const initialProfile = {
      // ── Identity ─────────────────────────────────────────────────────────
      uid: userId,
      displayName,
      firstName,
      email: data.email || '',
      photoURL: data.photoURL || '',
      provider: data.provider || 'google',
      emailVerified: data.emailVerified || false,

      // ── Current timezone (device timezone at sign-up) ────────────────────
      timezone: getUserTimezone() || null,

      // ── Birth data (filled during onboarding) ───────────────────────────
      birthData: {
        date: null,
        time: null,
        place: null,
        placeLat: null,
        placeLon: null,
        timezone: null
      },

      // ── Onboarding state ────────────────────────────────────────────────
      onboarding: {
        completed: false,
        shown: false,
        skipped: false,
        completedAt: null
      },
      onboardingCompleted: false,

      // ── Legal consent (filled when user accepts ToS) ─────────────────────
      consent: { ...DEFAULT_CONSENT },

      // ── Profile status ───────────────────────────────────────────────────
      profileActive: false,
      lastCalculated: null,

      // ── Preferences (defaults) ───────────────────────────────────────────
      preferences: { ...DEFAULT_PREFERENCES },

      // ── Notification defaults ────────────────────────────────────────────
      notifications: {
        pushEnabled: true,
        dailyReminder: true,
        streakProtection: true,
        emailEnabled: true,
        timezone: getUserTimezone() || null,
      },

      // ── Subscription ─────────────────────────────────────────────────────
      subscriptionTier: 'free',
      subscriptionUpdatedAt: null,

      // ── Activity tracking ────────────────────────────────────────────────
      firstActiveAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),

      // ── Sync / schema ─────────────────────────────────────────────────────
      syncToken: generateSyncToken(),
      version: PROFILE_SCHEMA_VERSION,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), initialProfile);

    logger.info('✅ [ProfileService] Created profile');
    return initialProfile;
  } catch (error) {
    logger.error('❌ [ProfileService] Error creating initial profile:', error);
    throw error;
  }
}

/**
 * Load user profile from Firestore (userProfiles only — v3.x schema).
 * Returns flat backward-compat fields alongside nested birthData.
 */
export async function loadProfile(userId) {
  if (!userId) {
    logger.warn('No user ID provided to loadProfile');
    return null;
  }

  try {
    const snap = await getDoc(doc(db, COLLECTIONS.USER_PROFILES, userId));
    if (!snap.exists()) return null;

    const data = snap.data();
    return {
      ...data,
      // Flat backward-compat fields derived from nested birthData
      birthDate: data.birthData?.date || data.birthDate || null,
      birthTime: data.birthData?.time || data.birthTime || null,
      birthPlace: data.birthData?.place || data.birthPlace || null,
      birthPlaceLat: data.birthData?.placeLat ?? data.birthPlaceLat ?? null,
      birthPlaceLon: data.birthData?.placeLon ?? data.birthPlaceLon ?? null,
      birthTimezone: data.birthData?.timezone || data.birthTimezone || null,
    };
  } catch (error) {
    logger.error('❌ [ProfileService] Error loading profile:', error);
    throw error;
  }
}

/**
 * Save birth data and profile status.
 * @param {string} userId
 * @param {Object} data - Flat or nested birthData format
 */
export async function saveProfile(userId, data) {
  if (!userId) throw new Error('User ID is required');

  try {
    const birthDateValue = data.birthData?.date || data.birthDate;
    const hasRequiredData = !!birthDateValue;

    const birthData = {
      date: data.birthData?.date || data.birthDate || null,
      time: data.birthData?.time || data.birthTime || null,
      place: data.birthData?.place || data.birthPlace || null,
      placeLat: data.birthData?.placeLat ?? data.birthPlaceLat ?? null,
      placeLon: data.birthData?.placeLon ?? data.birthPlaceLon ?? null,
      timezone: data.birthData?.timezone || data.birthTimezone || data.timezone || null
    };

    const hasOnboardingData = data.onboardingCompleted !== undefined ||
                              data.onboardingSkipped !== undefined ||
                              data.onboardingShown !== undefined;

    const profileUpdate = {
      uid: userId,
      birthData,
      profileActive: hasRequiredData,
      lastCalculated: hasRequiredData ? serverTimestamp() : null,
      syncToken: generateSyncToken(),
      updatedAt: serverTimestamp(),
      version: PROFILE_SCHEMA_VERSION
    };

    if (hasOnboardingData) {
      profileUpdate.onboarding = {
        completed: data.onboardingCompleted ?? false,
        shown: data.onboardingShown ?? true,
        skipped: data.onboardingSkipped ?? false,
        completedAt: data.onboardingCompleted ? serverTimestamp() : null
      };
      profileUpdate.onboardingCompleted = data.onboardingCompleted ?? false;
    }

    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      removeUndefined(profileUpdate),
      { merge: true }
    );

    return profileUpdate;
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving profile:', error);
    throw error;
  }
}

/**
 * Save onboarding state.
 */
export async function saveOnboardingState(userId, onboardingState) {
  if (!userId) throw new Error('User ID is required');

  try {
    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      {
        onboarding: {
          completed: onboardingState.completed ?? false,
          shown: onboardingState.shown ?? false,
          skipped: onboardingState.skipped ?? false,
          completedAt: onboardingState.completed ? serverTimestamp() : null
        },
        onboardingCompleted: onboardingState.completed ?? false,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving onboarding state:', error);
    throw error;
  }
}

/**
 * Record user's explicit legal consent (ToS, Privacy Policy, age verification).
 * Call this when the user actively accepts on the onboarding welcome screen.
 *
 * @param {string} userId
 * @param {Object} opts
 * @param {boolean} opts.termsAccepted
 * @param {boolean} opts.privacyAccepted
 * @param {boolean} opts.ageVerified - User confirmed they are 13+
 */
export async function saveConsent(userId, { termsAccepted, privacyAccepted, ageVerified }) {
  if (!userId) throw new Error('User ID is required');

  try {
    const now = serverTimestamp();
    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      {
        consent: {
          termsAcceptedAt: termsAccepted ? now : null,
          privacyPolicyAcceptedAt: privacyAccepted ? now : null,
          ageVerified: ageVerified || false,
          ageConsentAt: ageVerified ? now : null
        },
        updatedAt: now
      },
      { merge: true }
    );
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving consent:', error);
    throw error;
  }
}

/**
 * Update lastActiveAt timestamp. Throttled by callers — should not be called
 * on every page render, only on app init / login.
 */
export async function updateLastActive(userId) {
  if (!userId) return;

  try {
    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      { lastActiveAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (error) {
    // Non-fatal — don't surface to user
    logger.warn('[ProfileService] Failed to update lastActiveAt:', error);
  }
}

/**
 * Sync mutable identity fields from Firebase Auth into userProfiles.
 * Call once per session (not on every page refresh) to keep profile fresh.
 * Also updates lastActiveAt and current device timezone.
 *
 * @param {string} userId
 * @param {Object} authUser - Firebase Auth user object
 */
export async function syncProfileFromAuth(userId, authUser) {
  if (!userId || !authUser) return;

  try {
    const update = {
      emailVerified: authUser.emailVerified || false,
      timezone: getUserTimezone() || null,
      lastActiveAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Only overwrite identity fields if non-empty (don't blank out data)
    if (authUser.displayName) {
      update.displayName = authUser.displayName;
      update.firstName = authUser.displayName.split(' ')[0] || '';
    }
    if (authUser.photoURL) update.photoURL = authUser.photoURL;
    if (authUser.email)    update.email = authUser.email;

    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      update,
      { merge: true }
    );
  } catch (error) {
    // Non-fatal
    logger.warn('[ProfileService] Failed to sync profile from auth:', error);
  }
}

/**
 * Save comprehensive user profile (hexagram, astrology, etc.)
 */
export async function saveComprehensiveProfile(userId, hexagramProfile, basicProfile) {
  if (!userId || !hexagramProfile) {
    logger.warn('[ProfileService] Missing userId or hexagramProfile');
    return;
  }

  try {
    const sunSign = hexagramProfile.zodiacSign || hexagramProfile.westernAstrology?.sun?.sign || 'Unknown';
    const moonSign = hexagramProfile.westernAstrology?.moon?.sign || hexagramProfile.natalChart?.moon?.zodiacSign || 'Unknown';
    const risingSign = hexagramProfile.westernAstrology?.rising?.sign || 'Unknown';
    const hdType = hexagramProfile.humanDesign?.type || 'Unknown';
    const hdProfile = hexagramProfile.humanDesign?.profile || 'Unknown';
    const hdStrategy = hexagramProfile.humanDesign?.strategy || 'Unknown';
    const hdAuthority = hexagramProfile.humanDesign?.authority || 'Unknown';

    let aiDescription = `${sunSign} Sun`;
    if (moonSign !== 'Unknown') aiDescription += `, ${moonSign} Moon`;
    if (risingSign !== 'Unknown') aiDescription += `, ${risingSign} Rising`;
    if (hdType !== 'Unknown') aiDescription += `. Human Design ${hdType}`;
    if (hdProfile !== 'Unknown') aiDescription += ` with ${hdProfile} profile`;
    if (hdStrategy !== 'Unknown') aiDescription += `. Strategy: ${hdStrategy}`;
    if (hdAuthority !== 'Unknown') aiDescription += `. Authority: ${hdAuthority}`;

    const profileUpdate = {
      hexagramProfile,
      quickAccess: {
        sunSign,
        moonSign,
        risingSign,
        humanDesignType: hdType,
        humanDesignProfile: hdProfile,
        humanDesignStrategy: hdStrategy,
        humanDesignAuthority: hdAuthority,
        personalityGate: hexagramProfile.personality || 1,
        designGate: hexagramProfile.design || 2,
        lifePurposeGate: hexagramProfile.life_purpose || 1
      },
      aiContext: {
        description: aiDescription,
        personalityHexagram: hexagramProfile.personalityDetails?.name || 'Unknown',
        designHexagram: hexagramProfile.designDetails?.name || 'Unknown',
        natalChartComplete: !!(hexagramProfile.natalChart && hexagramProfile.natalChart.uranus),
        hasFullAstrology: !!(hexagramProfile.westernAstrology && hexagramProfile.humanDesign),
        hasBirthTime: !!(basicProfile?.birthTime || basicProfile?.birthData?.time),
        hasBirthPlace: !!(basicProfile?.birthPlace || basicProfile?.birthData?.place),
        precomputedContext: buildEnhancedAIContext(hexagramProfile)
      },
      calculationMethod: hexagramProfile.calculationMethod || 'simple',
      syncToken: generateSyncToken(),
      updatedAt: serverTimestamp(),
      version: PROFILE_SCHEMA_VERSION
    };

    await setDoc(
      doc(db, COLLECTIONS.USER_PROFILES, userId),
      removeUndefined(profileUpdate),
      { merge: true }
    );

    logger.info('✅ [ProfileService] Saved comprehensive profile');
  } catch (error) {
    logger.warn('⚠️ [ProfileService] Failed to save comprehensive profile:', error);
    throw error;
  }
}

// ============================================================================
// PREFERENCE MANAGEMENT
// ============================================================================

export async function loadPreferences(userId) {
  if (!userId) return { ...DEFAULT_PREFERENCES };
  try {
    const profile = await loadProfile(userId);
    return profile?.preferences || { ...DEFAULT_PREFERENCES };
  } catch (error) {
    logger.error('❌ [ProfileService] Error loading preferences:', error);
    return { ...DEFAULT_PREFERENCES };
  }
}

export async function saveInsightPreferences(userId, insightPrefs) {
  if (!userId) throw new Error('User ID is required');
  try {
    await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), {
      'preferences.insights.frequency':  insightPrefs.frequency  || 'balanced',
      'preferences.insights.paused':     insightPrefs.paused     || false,
      'preferences.insights.pausedUntil': insightPrefs.pausedUntil || null,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving insight preferences:', error);
    throw error;
  }
}

export async function savePrivacyPreferences(userId, privacyPrefs) {
  if (!userId) throw new Error('User ID is required');
  try {
    await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), {
      'preferences.privacy.dataRetention': privacyPrefs.dataRetention || '2_years',
      'preferences.privacy.shareAnalytics': privacyPrefs.shareAnalytics ?? false,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving privacy preferences:', error);
    throw error;
  }
}

export async function saveOCRPreferences(userId, ocrPrefs) {
  if (!userId) throw new Error('User ID is required');
  try {
    await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), {
      'preferences.ocr.keepOriginalImages': ocrPrefs.keepOriginalImages ?? true,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving OCR preferences:', error);
    throw error;
  }
}

export async function saveJournalPreferences(userId, journalPrefs) {
  if (!userId) throw new Error('User ID is required');
  try {
    await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), {
      'preferences.journal.defaultPrivacy':  journalPrefs.defaultPrivacy  || 'private',
      'preferences.journal.reminderEnabled': journalPrefs.reminderEnabled ?? false,
      'preferences.journal.reminderTime':    journalPrefs.reminderTime    || null,
      'preferences.enableWritingCompanion':  journalPrefs.enableWritingCompanion ?? true,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    logger.error('❌ [ProfileService] Error saving journal preferences:', error);
    throw error;
  }
}

// ============================================================================
// GDPR COMPLIANCE - Data Export & Deletion
// ============================================================================

export async function exportUserProfileData(userId) {
  if (!userId) throw new Error('User ID is required');
  try {
    const profile = await loadProfile(userId);
    return { exportedAt: new Date().toISOString(), userId, profile: profile || null };
  } catch (error) {
    logger.error('❌ [ProfileService] Error exporting user data:', error);
    throw error;
  }
}

/**
 * Delete all user profile data (GDPR Right to be Forgotten).
 * All preferences are now stored in userProfiles — one delete covers everything.
 */
export async function deleteUserProfileData(userId) {
  if (!userId) throw new Error('User ID is required');
  try {
    const { deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, COLLECTIONS.USER_PROFILES, userId));
    logger.info('✅ [ProfileService] Deleted all profile data for user:', userId);
  } catch (error) {
    logger.error('❌ [ProfileService] Error deleting user data:', error);
    throw error;
  }
}
